import React, { memo, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Box, useResponsiveValue } from '@machobear/design-system'
import { useVirtualizer } from '@tanstack/react-virtual'

import 'react-indiana-drag-scroll/dist/style.css'

import { breakpoints, colors } from '@/styles'
import useSessionComplete from '@/hooks/use-session-complete'
import { ChevronLeft, ChevronRight } from '@/components/icons'
import ListingCard from '@/components/listing-card'
import { useTermsDialogContext } from '@/components/terms-dialog/context'
import { Label } from '@/components/text'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${colors.grey48};
  margin: 0 -20px;
  width: calc(100% + 40px);

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100vw;
  }
`

const LabelWrapper = styled.div`
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ListingWrapper = styled.div`
  padding: 2px 20px;
  overflow: auto;
`

const ScrollButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 100%;
  border: 1px solid ${colors.grey88};
  background: ${props => (props.disabled ? colors.grey96 : colors.white)};
  color: ${props => (props.disabled ? colors.grey88 : colors.black)};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  transition: all 0.3s ease-in-out;
  &:hover {
    background: ${props => (props.disabled ? colors.grey96 : colors.grey96)};
  }
  svg {
    pointer-events: none;
  }
`

const VirutalizedCardWrapper = ({ children, cardWidth, translateX }) => (
  <Box
    position="absolute"
    top={0}
    label={0}
    height="full"
    style={{
      width: `${cardWidth}px`,
      transform: `translateX(${translateX}px)`
    }}
  >
    {children}
  </Box>
)

const FeaturedListings = memo(
  ({ listings = [], setHighlightedBuilding, setVisitedBuilding }) => {
    const { t } = useTranslation()
    const [rightEdge, setRightEdge] = useState(false)
    const [leftEdge, setLeftEdge] = useState(true)
    const titleText = t('b.listings.view.sponsored.text', 'Sponsored')
    const hasSession = useSessionComplete()
    const isMobile = useResponsiveValue()({
      desktop: false,
      tablet: false,
      mobile: true
    })

    const { isLoading: isLoadingAgreements } = useTermsDialogContext()
    const showSkeletonState =
      listings.length === 0 || (hasSession && isLoadingAgreements)

    const listingCardProps = {
      featured: true,
      variant: 'featured'
    }

    const scrollRef = useRef(null)
    const width = scrollRef.current?.offsetWidth
    const cardWidth = isMobile ? 270 : 320

    const columnVirtualizer = useVirtualizer({
      count: listings.length,
      getScrollElement: () => scrollRef.current,
      estimateSize: () => cardWidth,
      overscan: 3,
      horizontal: true,
      gap: isMobile ? 10 : 20
    })

    useEffect(() => {
      const c = scrollRef.current
      const handleScroll = () => {
        if (c) {
          const rightEdge = c.scrollWidth - c.clientWidth - c.scrollLeft
          const leftEdge = c.scrollLeft

          setRightEdge(rightEdge < 20)
          setLeftEdge(leftEdge < 20)
        }
      }

      c?.addEventListener('scroll', handleScroll)

      return () => {
        c?.removeEventListener('scroll', handleScroll)
      }
    }, [])

    useEffect(() => {
      columnVirtualizer.measure()
    }, [isMobile, columnVirtualizer])

    const handleScroll = scrollOffset => {
      if (scrollRef.current) {
        scrollRef.current.scrollBy({ left: scrollOffset, behavior: 'smooth' })
      }
    }

    return (
      <Wrapper>
        <LabelWrapper>
          <Label fontSize="14px">{titleText}</Label>
          <ButtonWrapper>
            <ScrollButton
              onClick={() => handleScroll(-width)}
              disabled={leftEdge}
              id="city-page-sponsor-listing-back-click"
              title="Scroll Left"
              aria-label="Scroll Left"
            >
              <ChevronLeft />
            </ScrollButton>
            <ScrollButton
              onClick={() => handleScroll(width)}
              disabled={rightEdge}
              id="city-page-sponsor-listing-fwd-click"
              title="Scroll Right"
              aria-label="Scroll Right"
            >
              <ChevronRight />
            </ScrollButton>
          </ButtonWrapper>
        </LabelWrapper>
        <ListingWrapper ref={scrollRef}>
          <Box
            aria-label="sponsored-listings"
            data-impression-container="true"
            position="relative"
            style={{
              height: '350px',
              width: columnVirtualizer.getTotalSize()
            }}
          >
            {showSkeletonState
              ? Array.from({ length: 4 }).map((_, index) => (
                  <VirutalizedCardWrapper
                    key={index}
                    translateX={(cardWidth + 20) * index}
                    cardWidth={cardWidth}
                  >
                    <ListingCard skeleton {...listingCardProps} />
                  </VirutalizedCardWrapper>
                ))
              : columnVirtualizer.getVirtualItems().map(virtualItem => {
                  const listing = listings[virtualItem.index]
                  const buildingId = listing.building_id

                  return (
                    <VirutalizedCardWrapper
                      key={virtualItem.index}
                      translateX={virtualItem.start}
                      cardWidth={cardWidth}
                    >
                      <ListingCard
                        listing={listing}
                        key={listing.listing_id}
                        favouriteButton
                        noSchema
                        newTab
                        loading={showSkeletonState}
                        featuredPosition={virtualItem.index + 1}
                        onMouseEnter={() => setHighlightedBuilding(buildingId)}
                        onMouseLeave={() => setHighlightedBuilding('')}
                        onClick={() => setVisitedBuilding(buildingId)}
                        trackImpression={{
                          enabled: true,
                          page: 'listings',
                          section: 'listing-card',
                          sponsored: true
                        }}
                        {...listingCardProps}
                      />
                    </VirutalizedCardWrapper>
                  )
                })}
          </Box>
        </ListingWrapper>
      </Wrapper>
    )
  }
)

FeaturedListings.displayName = 'FeaturedListings'

export default FeaturedListings
